import { REGEXP_ONLY_DIGITS } from "input-otp";
import { twMerge } from "tailwind-merge";

import { Button, InputOTP } from "~/common/components/ui";
import { DEFAULT_CODE_LENGTH } from "~/common/constants/verificationCode";

interface CodeVerificationOptions {
  codeLength: number;
}

interface CodeVerificationProps {
  instructions?: string;
  remainingTime?: number;
  inCooldownTime?: boolean;
  onChange?: (code: string) => void;
  onComplete?: () => void;
  onRetry?: () => void;
  disabled?: boolean;
  error?: string;
  value?: string;
  options?: Partial<CodeVerificationOptions>;
}

const defaultOptions: CodeVerificationOptions = {
  codeLength: DEFAULT_CODE_LENGTH,
};

export const CodeVerification = ({
  instructions,
  remainingTime,
  inCooldownTime,
  onChange,
  onComplete,
  onRetry,
  options,
  disabled = false,
  error,
  value = "",
}: CodeVerificationProps) => {
  const { codeLength } = { ...defaultOptions, ...options };

  const handleResendCode = () => {
    onRetry?.();
  };

  const handleCodeChange = (code: string) => {
    onChange?.(code);

    const isComplete = code.length === codeLength;
    if (isComplete) {
      onComplete?.();
    }
  };

  const getResendButtonLabel = () => {
    if (disabled) {
      return "...";
    }

    if (inCooldownTime) {
      return `Resend code (${remainingTime}s)`;
    }

    return "Resend code";
  };

  return (
    <>
      {instructions && <p className="text-gray-800">{instructions}</p>}
      <InputOTP.Root
        autoFocus
        maxLength={codeLength}
        value={value}
        onChange={handleCodeChange}
        pattern={REGEXP_ONLY_DIGITS}
        disabled={disabled}
      >
        <InputOTP.Group>
          {Array.from({ length: codeLength }).map((_, index) => (
            <InputOTP.Slot
              key={index}
              index={index}
              className={twMerge(
                "sm:w-12",
                !!error &&
                  "border-complementary-error-700 bg-[#FCF5F4] text-complementary-error-700",
              )}
            />
          ))}
        </InputOTP.Group>
      </InputOTP.Root>

      {!!error && (
        <p className="text-sm text-complementary-error-700">{error}</p>
      )}

      <Button
        disabled={inCooldownTime ?? disabled}
        onClick={handleResendCode}
        type="button"
        variant="tertiary-link"
        className="mx-auto w-fit text-blue-800 disabled:cursor-not-allowed disabled:text-gray-10"
      >
        {getResendButtonLabel()}
      </Button>
    </>
  );
};
